/**
 * Email Ad Validation Data Hook
 * Manages state and data flow for the Email Ad Validation feature
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { formatDateToDDMMYY } from '../../../constants/utils';
import useEmailAdValidationApi from './useEmailAdValidationApi';

export const useEmailAdValidationData = () => {
  const [activeView, setActiveView] = useState('table'); // 'form' or 'table'
  const [historicalRuns, setHistoricalRuns] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    emailType: '',
  });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    loading: false,
  });
  const hasLoadedData = useRef(false); // Prevent multiple API calls

  const {
    loading,
    error,
    clearError,
    submitValidationRun,
    fetchHistoricalRuns,
    RerunValidationRun,
    deleteValidationRun,
  } = useEmailAdValidationApi();

  // Load historical runs on component mount (with protection against multiple calls)
  useEffect(() => {
    if (hasLoadedData.current || loading) {
      return; // Prevent multiple calls
    }

    const loadData = async (page = 0) => {
      hasLoadedData.current = true;

      try {
        // Include current filters in initial load
        const filterParams = {
          page: page + 1, // API expects 1-based pagination
          ...(filters.status && { status: filters.status }),
          ...(filters.emailType && { emailType: filters.emailType }),
        };

        const data = await fetchHistoricalRuns(filterParams);

        if (data && typeof data === 'object') {
          let runs = [];
          if (Array.isArray(data.results)) {
            runs = data.results;
          } else if (Array.isArray(data)) {
            runs = data;
          }
          const total = data.count || data.total || runs.length;

          const transformedData = runs.map((run) => ({
            ...run,
            runName: run.run_name || run.runName || 'Unnamed Run',
            submitDate: new Date(run.created_at || run.submit_date || Date.now()),
            fromEmail: run.from_email || run.fromEmail || '',
            toEmail: run.to_email || run.toEmail || '',
            emailType: run.email_type || run.emailType || '',
            offerMatrix: run.promotion_offer_matrix || run.offerMatrix || '',
            startDate: new Date(run.start_date || run.startDate || Date.now()),
            endDate: new Date(run.end_date || run.endDate || Date.now()),
            status: run.status || 'QUEUED',
            subject: run.subject || '',
            dateRange:
              run.start_date && run.end_date
                ? `${formatDateToDDMMYY(new Date(run.start_date))} - ${formatDateToDDMMYY(new Date(run.end_date))}`
                : 'No date range',
          }));

          setHistoricalRuns(transformedData);
          setTotalCount(total);
        } else {
          setHistoricalRuns([]);
          setTotalCount(0);
        }
      } catch (err) {
        hasLoadedData.current = false; // Reset on error to allow retry
        setHistoricalRuns([]);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadData(currentPage);
  }, []); // Empty dependency array

  // Handle form submission
  const handleFormSubmit = useCallback(
    async (formData) => {
      try {
        clearError();

        // Use real API call to POST to EMAIL_AD_VALIDATION_RUNS
        const result = await submitValidationRun(formData);

        // Helper function to safely handle date inputs from DatePickerMui
        const createSafeDate = (dateInput) => {
          if (!dateInput) {
            return new Date();
          }

          // If it's already a Date object, return it
          if (dateInput instanceof Date) {
            return isNaN(dateInput.getTime()) ? new Date() : dateInput;
          }

          // If it's a string in dd/MM/yy format, parse it correctly
          if (typeof dateInput === 'string' && dateInput.includes('/')) {
            const parts = dateInput.split('/');
            if (parts.length === 3) {
              const day = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1; // Months are 0-indexed
              const year = parseInt(parts[2], 10);
              // Assume 2000s for 2-digit years
              const fullYear = year < 100 ? 2000 + year : year;
              const parsedDate = new Date(fullYear, month, day);
              return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
            }
          }

          // Try standard Date parsing as fallback
          const parsedDate = new Date(dateInput);
          return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
        };

        // Safely create date objects
        const startDateObj = createSafeDate(formData.startDate);
        const endDateObj = createSafeDate(formData.endDate);

        // Add the new run to the list
        const newRun = {
          id: result.id || Date.now(),
          runName: formData.runName || `Run ${Date.now()}`,
          submitDate: new Date(),
          fromEmail: formData.fromEmail,
          toEmail: formData.toEmail,
          subject: formData.subject,
          startDate: startDateObj,
          endDate: endDateObj,
          emailType: formData.emailType,
          offerMatrix: formData.offerMatrix,
          status: result.status || 'In Progress',
          results: result.results || '',
          dateRange: `${formatDateToDDMMYY(startDateObj)} - ${formatDateToDDMMYY(endDateObj)}`,
        };

        setHistoricalRuns((prev) => [newRun, ...prev]);

        return { success: true, message: 'Validation run submitted successfully' };
      } catch (err) {
        console.error('Failed to submit validation run:', err);
        return {
          success: false,
          message: err.message || 'Failed to submit validation run. Please try again.',
        };
      }
    },
    [clearError, submitValidationRun]
  );

  // Handle rerun validation
  const handleRerunValidation = useCallback(
    async (runId) => {
      try {
        clearError();

        await RerunValidationRun(runId);

        // Refresh the historical runs to get updated status
        try {
          const data = await fetchHistoricalRuns();

          if (Array.isArray(data)) {
            const transformedData = data.map((run) => ({
              ...run,
              runName: run.run_name || run.runName || 'Unnamed Run',
              submitDate: new Date(run.created_at || run.submit_date || Date.now()),
              fromEmail: run.from_email || run.fromEmail || '',
              toEmail: run.to_email || run.toEmail || '',
              emailType: run.email_type || run.emailType || '',
              offerMatrix: run.promotion_offer_matrix || run.offerMatrix || '',
              startDate: new Date(run.start_date || run.startDate || Date.now()),
              endDate: new Date(run.end_date || run.endDate || Date.now()),
              status: run.status || 'PENDING',
              subject: run.subject || '',
              dateRange:
                run.start_date && run.end_date
                  ? `${formatDateToDDMMYY(new Date(run.start_date))} - ${formatDateToDDMMYY(new Date(run.end_date))}`
                  : 'No date range',
            }));

            setHistoricalRuns(transformedData);
          }
        } catch (refreshErr) {
          console.warn('Failed to refresh data after rerun:', refreshErr);
        }

        return { success: true, message: 'Validation run restarted successfully' };
      } catch (err) {
        throw err;
      }
    },
    [clearError, RerunValidationRun, fetchHistoricalRuns]
  );

  // Handle delete validation
  const handleDeleteValidation = useCallback(
    async (runId) => {
      try {
        clearError();

        // Set loading state in dialog
        setConfirmDialog((prev) => ({ ...prev, loading: true }));

        await deleteValidationRun(runId);

        // Check if this is the last item on the current page
        const currentPageItems = historicalRuns.length;
        const isLastItemOnPage = currentPageItems === 1;
        const shouldGoToPreviousPage = isLastItemOnPage && currentPage > 0;

        if (shouldGoToPreviousPage) {
          // Navigate to previous page and fetch data
          const newPage = currentPage - 1;
          setCurrentPage(newPage);
          hasLoadedData.current = false; // Allow new API call

          try {
            const data = await fetchHistoricalRuns({ page: newPage + 1 }); // API expects 1-based pagination

            if (data && typeof data === 'object') {
              let runs = [];
              if (Array.isArray(data.results)) {
                runs = data.results;
              } else if (Array.isArray(data)) {
                runs = data;
              }
              const total = data.count || data.total || runs.length;

              const transformedData = runs.map((run) => ({
                ...run,
                runName: run.run_name || run.runName || 'Unnamed Run',
                submitDate: new Date(run.created_at || run.submit_date || Date.now()),
                fromEmail: run.from_email || run.fromEmail || '',
                toEmail: run.to_email || run.toEmail || '',
                emailType: run.email_type || run.emailType || '',
                offerMatrix: run.promotion_offer_matrix || run.offerMatrix || '',
                startDate: new Date(run.start_date || run.startDate || Date.now()),
                endDate: new Date(run.end_date || run.endDate || Date.now()),
                status: run.status || 'PENDING',
                subject: run.subject || '',
                dateRange:
                  run.start_date && run.end_date
                    ? `${formatDateToDDMMYY(new Date(run.start_date))} - ${formatDateToDDMMYY(new Date(run.end_date))}`
                    : 'No date range',
              }));

              setHistoricalRuns(transformedData);
              setTotalCount(total);
            } else {
              // API returned empty data
              setHistoricalRuns([]);
              setTotalCount(0);
            }
          } catch (fetchErr) {
            console.error('Failed to fetch previous page after deletion:', fetchErr);
            // Fallback: just remove the item locally
            setHistoricalRuns((prev) => prev.filter((run) => run.id !== runId));
            setTotalCount((prev) => Math.max(0, prev - 1));
          }
        } else {
          // Remove the deleted run from the current data
          setHistoricalRuns((prev) => prev.filter((run) => run.id !== runId));
          setTotalCount((prev) => Math.max(0, prev - 1));
        }

        // Close dialog
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, loading: false });

        return { success: true, message: 'Validation run deleted successfully' };
      } catch (err) {
        // Reset dialog loading state on error
        setConfirmDialog((prev) => ({ ...prev, loading: false }));
        throw err;
      }
    },
    [clearError, deleteValidationRun, historicalRuns.length, currentPage, fetchHistoricalRuns]
  );

  // Handle action clicks from table
  const handleTableAction = useCallback(
    async (action, run) => {
      try {
        clearError();

        switch (action) {
          case 'view':
            // Navigate to view results or open modal
            break;

          case 'edit':
            // Pre-populate form with run data
            setActiveView('form');
            break;

          case 'rerun':
            // Rerun the validation
            await handleRerunValidation(run.id);
            break;

          case 'delete':
            // Show confirmation dialog for delete
            setConfirmDialog({
              open: true,
              title: 'Delete Validation Run',
              message: `Are you sure you want to delete "${run.runName || run.run_name || 'this validation run'}"? This action cannot be undone.`,
              onConfirm: () => handleDeleteValidation(run.id),
              loading: false,
            });
            break;

          default:
            // Unknown action
            break;
        }
      } catch (err) {
        // Action failed - handle silently or show user feedback
      }
    },
    [clearError, handleRerunValidation, handleDeleteValidation]
  );

  // Handle page changes
  const handlePageChange = useCallback(
    async (newPage) => {
      try {
        setCurrentPage(newPage);
        hasLoadedData.current = false; // Allow new API call

        // Include current filters in pagination
        const filterParams = {
          page: newPage + 1, // API expects 1-based pagination
          ...(filters.status && { status: filters.status }),
          ...(filters.emailType && { emailType: filters.emailType }),
        };

        const data = await fetchHistoricalRuns(filterParams);

        if (data && typeof data === 'object') {
          let runs = [];
          if (Array.isArray(data.results)) {
            runs = data.results;
          } else if (Array.isArray(data)) {
            runs = data;
          }
          const total = data.count || data.total || runs.length;

          const transformedData = runs.map((run) => ({
            ...run,
            runName: run.run_name || run.runName || 'Unnamed Run',
            submitDate: new Date(run.created_at || run.submit_date || Date.now()),
            fromEmail: run.from_email || run.fromEmail || '',
            toEmail: run.to_email || run.toEmail || '',
            emailType: run.email_type || run.emailType || '',
            offerMatrix: run.promotion_offer_matrix || run.offerMatrix || '',
            startDate: new Date(run.start_date || run.startDate || Date.now()),
            endDate: new Date(run.end_date || run.endDate || Date.now()),
            status: run.status || 'PENDING',
            subject: run.subject || '',
            dateRange:
              run.start_date && run.end_date
                ? `${formatDateToDDMMYY(new Date(run.start_date))} - ${formatDateToDDMMYY(new Date(run.end_date))}`
                : 'No date range',
          }));

          setHistoricalRuns(transformedData);
          setTotalCount(total);
        }
      } catch (err) {
        console.error('Failed to load page:', err);
      }
    },
    [fetchHistoricalRuns, filters]
  );

  // Handle filter changes - single filter
  const handleFilterChange = useCallback(
    async (filterType, value) => {
      try {
        const newFilters = {
          ...filters,
          [filterType]: value,
        };

        setFilters(newFilters);
        setCurrentPage(0); // Reset to first page when filtering
        hasLoadedData.current = false; // Allow new API call

        // Prepare filter parameters for API call
        const filterParams = {
          page: 1, // Start from first page
          ...(newFilters.status && { status: newFilters.status }),
          ...(newFilters.emailType && { emailType: newFilters.emailType }),
        };

        const data = await fetchHistoricalRuns(filterParams);

        if (data && typeof data === 'object') {
          let runs = [];
          if (Array.isArray(data.results)) {
            runs = data.results;
          } else if (Array.isArray(data)) {
            runs = data;
          }
          const total = data.count || data.total || runs.length;

          const transformedData = runs.map((run) => ({
            ...run,
            runName: run.run_name || run.runName || 'Unnamed Run',
            submitDate: new Date(run.created_at || run.submit_date || Date.now()),
            fromEmail: run.from_email || run.fromEmail || '',
            toEmail: run.to_email || run.toEmail || '',
            emailType: run.email_type || run.emailType || '',
            offerMatrix: run.promotion_offer_matrix || run.offerMatrix || '',
            startDate: new Date(run.start_date || run.startDate || Date.now()),
            endDate: new Date(run.end_date || run.endDate || Date.now()),
            status: run.status || 'QUEUED',
            subject: run.subject || '',
            dateRange:
              run.start_date && run.end_date
                ? `${formatDateToDDMMYY(new Date(run.start_date))} - ${formatDateToDDMMYY(new Date(run.end_date))}`
                : 'No date range',
          }));

          setHistoricalRuns(transformedData);
          setTotalCount(total);
        } else {
          setHistoricalRuns([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error('Failed to apply filter:', err);
      }
    },
    [filters, fetchHistoricalRuns]
  );

  // Handle multiple filters at once - more efficient
  const handleFiltersChange = useCallback(
    async (newFilters) => {
      try {
        setFilters(newFilters);
        setCurrentPage(0); // Reset to first page when filtering
        hasLoadedData.current = false; // Allow new API call

        // Prepare filter parameters for API call
        const filterParams = {
          page: 1, // Start from first page
          ...(newFilters.status && { status: newFilters.status }),
          ...(newFilters.emailType && { emailType: newFilters.emailType }),
        };

        const data = await fetchHistoricalRuns(filterParams);

        if (data && typeof data === 'object') {
          let runs = [];
          if (Array.isArray(data.results)) {
            runs = data.results;
          } else if (Array.isArray(data)) {
            runs = data;
          }
          const total = data.count || data.total || runs.length;

          const transformedData = runs.map((run) => ({
            ...run,
            runName: run.run_name || run.runName || 'Unnamed Run',
            submitDate: new Date(run.created_at || run.submit_date || Date.now()),
            fromEmail: run.from_email || run.fromEmail || '',
            toEmail: run.to_email || run.toEmail || '',
            emailType: run.email_type || run.emailType || '',
            offerMatrix: run.promotion_offer_matrix || run.offerMatrix || '',
            startDate: new Date(run.start_date || run.startDate || Date.now()),
            endDate: new Date(run.end_date || run.endDate || Date.now()),
            status: run.status || 'QUEUED',
            subject: run.subject || '',
            dateRange:
              run.start_date && run.end_date
                ? `${formatDateToDDMMYY(new Date(run.start_date))} - ${formatDateToDDMMYY(new Date(run.end_date))}`
                : 'No date range',
          }));

          setHistoricalRuns(transformedData);
          setTotalCount(total);
        } else {
          setHistoricalRuns([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error('Failed to apply filters:', err);
      }
    },
    [fetchHistoricalRuns]
  );

  // Clear all filters
  const clearFilters = useCallback(async () => {
    try {
      const clearedFilters = {
        status: '',
        emailType: '',
      };

      setFilters(clearedFilters);
      setCurrentPage(0); // Reset to first page when clearing filters
      hasLoadedData.current = false; // Allow new API call

      // Fetch data without any filters
      const data = await fetchHistoricalRuns({ page: 1 });

      if (data && typeof data === 'object') {
        let runs = [];
        if (Array.isArray(data.results)) {
          runs = data.results;
        } else if (Array.isArray(data)) {
          runs = data;
        }
        const total = data.count || data.total || runs.length;

        const transformedData = runs.map((run) => ({
          ...run,
          runName: run.run_name || run.runName || 'Unnamed Run',
          submitDate: new Date(run.created_at || run.submit_date || Date.now()),
          fromEmail: run.from_email || run.fromEmail || '',
          toEmail: run.to_email || run.toEmail || '',
          emailType: run.email_type || run.emailType || '',
          offerMatrix: run.promotion_offer_matrix || run.offerMatrix || '',
          startDate: new Date(run.start_date || run.startDate || Date.now()),
          endDate: new Date(run.end_date || run.endDate || Date.now()),
          status: run.status || 'QUEUED',
          subject: run.subject || '',
          dateRange:
            run.start_date && run.end_date
              ? `${formatDateToDDMMYY(new Date(run.start_date))} - ${formatDateToDDMMYY(new Date(run.end_date))}`
              : 'No date range',
        }));

        setHistoricalRuns(transformedData);
        setTotalCount(total);
      } else {
        setHistoricalRuns([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Failed to clear filters:', err);
    }
  }, [fetchHistoricalRuns]);

  // Switch views
  const switchToForm = useCallback(() => {
    setActiveView('form');
  }, []);

  const switchToTable = useCallback(async () => {
    setActiveView('table');

    // Refresh the historical runs data when navigating back to table
    try {
      const data = await fetchHistoricalRuns();

      if (Array.isArray(data)) {
        const transformedData = data.map((run) => ({
          ...run,
          runName: run.run_name || run.runName || 'Unnamed Run',
          submitDate: new Date(run.created_at || run.submit_date || Date.now()),
          fromEmail: run.from_email || run.fromEmail || '',
          toEmail: run.to_email || run.toEmail || '',
          emailType: run.email_type || run.emailType || '',
          offerMatrix: run.promotion_offer_matrix || run.offerMatrix || '',
          startDate: new Date(run.start_date || run.startDate || Date.now()),
          endDate: new Date(run.end_date || run.endDate || Date.now()),
          status: run.status || 'PENDING',
          subject: run.subject || '',
          dateRange:
            run.start_date && run.end_date
              ? `${formatDateToDDMMYY(new Date(run.start_date))} - ${formatDateToDDMMYY(new Date(run.end_date))}`
              : 'No date range',
        }));

        setHistoricalRuns(transformedData);
      }
    } catch (err) {
      // Silently handle refresh errors - existing data will remain
      console.warn('Failed to refresh data when switching to table:', err);
    }
  }, [fetchHistoricalRuns]);

  // Dialog control functions
  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null, loading: false });
  }, []);

  const confirmDialogAction = useCallback(async () => {
    if (confirmDialog.onConfirm) {
      await confirmDialog.onConfirm();
    }
  }, [confirmDialog.onConfirm]);

  return {
    // State
    activeView,
    historicalRuns,
    totalCount,
    currentPage,
    filters,
    loading,
    error,
    isInitialLoad,
    confirmDialog,

    // Actions
    handleFormSubmit,
    handleTableAction,
    handleRerunValidation,
    handleDeleteValidation,
    handlePageChange,
    handleFilterChange,
    handleFiltersChange,
    clearFilters,
    switchToForm,
    switchToTable,
    clearError,
    closeConfirmDialog,
    confirmDialogAction,
  };
};

export default useEmailAdValidationData;
